import { useCallback, useMemo, useState, type FC } from 'react';
import { Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import PatientSelectBox from 'components/molecules/patient-select-box';
import TimeframePicker from 'components/molecules/oneplaform/timeframe-filter/timeframe-picker';
import FilterButton from 'components/atoms/filter-button';
import RpmDashboardFilterPopover from 'components/molecules/rpm-dashboard-filter-popover';
import dayjs from 'dayjs';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats, PresetKeyEnum } from 'infrastructure/enums';

import styles from './styles.module.scss';

import type { Dayjs } from 'dayjs';
import type { RangePickerValue } from 'components/atoms/base-range-datepicker';
import type { TOverviewDashboardFilter } from 'infrastructure/hooks/rpm/overview-dashboard';

interface RpmDashboardHeaderActionsProps {
  filterValues?: {
    patientGuid?: string;
    doctorGuid?: string | string[];
    clinicGuid?: string;
  };
  onFilterChange?: (key: string, value: any) => void;
  onPatientLoaded?: (patientName: string | null) => void;
  onClinicLoaded?: (clinicName: string | null) => void;
  onAgencyDoctorLoaded?: (agencyDoctorName: string | null) => void;
  dateValues?: RangePickerValue;
  onDateChange?: (
    dates: [Date | null, Date | null],
    dateString: [string, string],
    info: { range?: 'start' | 'end' },
    presetKey?: string,
  ) => void;
  filterCount?: number;
  filter?: TOverviewDashboardFilter;
  updateFilter?: (newFilter: Partial<TOverviewDashboardFilter>) => void;
  resetFilter?: () => void;
}

const RpmDashboardHeaderActions: FC<RpmDashboardHeaderActionsProps> = ({
  filterValues,
  onFilterChange,
  onPatientLoaded,
  onClinicLoaded,
  onAgencyDoctorLoaded,
  dateValues,
  filterCount = 0,
  onDateChange,
  filter,
  updateFilter,
  resetFilter,
}) => {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);

  const handlePatientSelect = useCallback(
    (patientGuid: string, patientName: string) => {
      if (onFilterChange) {
        onFilterChange('patientGuid', patientGuid);
      }
      if (onPatientLoaded) {
        onPatientLoaded(patientName);
      }
    },
    [onFilterChange, onPatientLoaded],
  );

  const handlePatientLoaded = useCallback(
    (items: any[], selected: any) => {
      if (onPatientLoaded && selected?.label) {
        onPatientLoaded(selected.label);
      }
    },
    [onPatientLoaded],
  );

  const selectedRange = useMemo<[Dayjs | null, Dayjs | null]>(() => {
    if (dateValues && dateValues[0] && dateValues[1]) {
      return [dayjs(dateValues[0]), dayjs(dateValues[1])];
    }
    return [null, null];
  }, [dateValues]);

  const handleTimeframeChange = useCallback(
    (
      dates: null | (Dayjs | null)[],
      dateStrings: string[],
      presetKey?: string | null,
    ) => {
      if (onDateChange) {
        const nativeDates: [Date | null, Date | null] = [
          dates?.[0]?.toDate() || null,
          dates?.[1]?.toDate() || null,
        ];
        onDateChange(
          nativeDates,
          [dateStrings[0] || '', dateStrings[1] || ''],
          { range: dates?.[0] && dates?.[1] ? undefined : 'start' },
          presetKey || undefined,
        );
      }
    },
    [onDateChange],
  );

  const popoverContent = useMemo(() => {
    if (filter && updateFilter && resetFilter) {
      return (
        <RpmDashboardFilterPopover
          filter={filter}
          updateFilter={updateFilter}
          count={filterCount}
          resetFilter={resetFilter}
          onClinicLoaded={onClinicLoaded}
          onAgencyDoctorLoaded={onAgencyDoctorLoaded}
        />
      );
    }
    return null;
  }, [
    filter,
    updateFilter,
    resetFilter,
    filterCount,
    onClinicLoaded,
    onAgencyDoctorLoaded,
  ]);

  return (
    <Flex align="center" gap={12} className={styles.actions}>
      <div className={styles['search-wrapper']}>
        <PatientSelectBox
          placeholder={t('labels.searchPatient', 'Search Patient Name')}
          value={filterValues?.patientGuid}
          onSelectPatient={handlePatientSelect}
          onLoaded={handlePatientLoaded}
          doctorGuid={filterValues?.doctorGuid}
          clinicGuid={filterValues?.clinicGuid}
          allowClear
          plaintext
          className={styles['search-input']}
          labelRender={(el) => {
            const date = DateFormatter({
              date: el.record?.birthDate,
              format: DateFormats['MM-dd-yyyy'],
              toUTC: true,
            });
            return `${el.label} ${date}`;
          }}
        />
      </div>

      <div className={styles['timeframe-wrapper']}>
        <TimeframePicker
          selectedRange={selectedRange}
          onRangeChange={handleTimeframeChange}
          minRangeDays={0}
          restrictToSameMonth
          clearSelection={() => {
            if (onDateChange) {
              onDateChange(
                [null, null],
                ['', ''],
                {},
                PresetKeyEnum.CurrentMonth,
              );
            }
          }}
        />
      </div>
      <FilterButton
        count={filterCount}
        popoverContent={popoverContent}
        open={opened}
        onOpenChange={setOpened}
      />
    </Flex>
  );
};

export default RpmDashboardHeaderActions;
